import styled from '@emotion/styled'
import gql from 'graphql-tag'
import React, { useCallback, useContext, useState } from 'react'
import { useApolloClient } from 'react-apollo'
import { Helmet } from 'react-helmet'
import { Link, Redirect, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '../../../api/graphql'
import { CurrentUserContext, CurrentUserQuery, currentUserFragment } from '../../../providers/CurrentUserContext'
import Routes from '../../../startup/routes'
import Loading from '../misc/Loading'
import { Hugeicon } from '../../../icons/Hugeicon'

const ORGANIZATION_INVITATION_QUERY = gql`
  query OrganizationInvitation($token: String!) {
    organizationInvitation(token: $token) {
      id
      organizationName
      organizationLogoPublicId
      invitedEmail
      invitedName
      invitedByName
      status
      expiresAt
    }
  }
`

const ACCEPT_INVITATION_MUTATION = gql`
  mutation OrganizationMembershipAccept($input: OrganizationMembershipAcceptInput!) {
    organizationMembershipAccept(input: $input) {
      organizationMembership {
        id
        organization {
          id
          name
        }
        user {
          ...currentUserFragment
        }
      }
      errors {
        attribute
        message
      }
    }
  }
  ${currentUserFragment}
`

type RouteParams = {
  token: string
}

type InvitationData = {
  id: string
  organizationName: string
  organizationLogoPublicId: string | null
  invitedEmail: string | null
  invitedName: string | null
  invitedByName: string | null
  status: 'pending' | 'accepted' | 'expired' | 'deleted'
  expiresAt: string | null
}

const AcceptInvitation: React.FC = () => {
  const { token } = useParams<RouteParams>()
  const client = useApolloClient()
  const { currentUser, loading: userLoading } = useContext(CurrentUserContext)
  const [acceptLoading, setAcceptLoading] = useState(false)
  const [acceptError, setAcceptError] = useState<string | null>(null)
  const [acceptSuccess, setAcceptSuccess] = useState(false)

  const { data, loading, error } = useQuery(ORGANIZATION_INVITATION_QUERY, {
    variables: { token },
    skip: !token,
  })

  const [acceptInvitation] = useMutation(ACCEPT_INVITATION_MUTATION)

  const invitation: InvitationData | null = data?.organizationInvitation

  const handleAccept = useCallback(async () => {
    setAcceptLoading(true)
    setAcceptError(null)

    try {
      const result = await acceptInvitation({
        variables: { input: { token } },
      })

      const response = result.data?.organizationMembershipAccept
      if (response?.errors?.length > 0) {
        setAcceptError(response.errors.map((e: { message: string }) => e.message).join(', '))
        setAcceptLoading(false)
        return
      }

      // Update current user in cache with new membership
      if (response?.organizationMembership?.user) {
        client.writeQuery({
          query: CurrentUserQuery,
          data: { currentUser: response.organizationMembership.user },
        })
      }

      setAcceptSuccess(true)
      setAcceptLoading(false)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to accept invitation'
      setAcceptError(errorMessage)
      setAcceptLoading(false)
    }
  }, [acceptInvitation, client, token])

  // Show loading state
  if (loading || userLoading) {
    return (
      <StyledOverlay>
        <Loading className="isCentered isDark" />
      </StyledOverlay>
    )
  }

  // Handle GraphQL errors
  if (error) {
    return (
      <StyledOverlay>
        <StyledCard>
          <StyledErrorIcon>
            <XCircleIcon />
          </StyledErrorIcon>
          <StyledTitle>Error</StyledTitle>
          <StyledMessage>Something went wrong while loading this invitation.</StyledMessage>
          <StyledLink to={Routes.signIn()}>Go to Sign In</StyledLink>
        </StyledCard>
      </StyledOverlay>
    )
  }

  // Invalid token
  if (!invitation) {
    return (
      <StyledOverlay>
        <Helmet>
          <title>Invalid Invitation</title>
        </Helmet>
        <StyledCard>
          <StyledErrorIcon>
            <XCircleIcon />
          </StyledErrorIcon>
          <StyledTitle>Invalid Invitation</StyledTitle>
          <StyledMessage>This invitation link is invalid or has been removed.</StyledMessage>
          <StyledLink to={Routes.signIn()}>Go to Sign In</StyledLink>
        </StyledCard>
      </StyledOverlay>
    )
  }

  // Expired invitation
  if (invitation.status === 'expired') {
    return (
      <StyledOverlay>
        <Helmet>
          <title>Invitation Expired</title>
        </Helmet>
        <StyledCard>
          <StyledWarningIcon>
            <ClockIcon />
          </StyledWarningIcon>
          <StyledTitle>Invitation Expired</StyledTitle>
          <StyledMessage>
            This invitation to join <strong>{invitation.organizationName}</strong> has expired.
          </StyledMessage>
          <StyledSubMessage>Please contact the organization to request a new invitation.</StyledSubMessage>
          <StyledLink to={Routes.signIn()}>Go to Sign In</StyledLink>
        </StyledCard>
      </StyledOverlay>
    )
  }

  // Deleted/revoked invitation
  if (invitation.status === 'deleted') {
    return (
      <StyledOverlay>
        <Helmet>
          <title>Invitation Revoked</title>
        </Helmet>
        <StyledCard>
          <StyledErrorIcon>
            <XCircleIcon />
          </StyledErrorIcon>
          <StyledTitle>Invitation Revoked</StyledTitle>
          <StyledMessage>
            This invitation to join <strong>{invitation.organizationName}</strong> has been revoked.
          </StyledMessage>
          <StyledSubMessage>Please contact the organization if you believe this is an error.</StyledSubMessage>
          <StyledLink to={Routes.signIn()}>Go to Sign In</StyledLink>
        </StyledCard>
      </StyledOverlay>
    )
  }

  // Already accepted
  if (invitation.status === 'accepted') {
    return (
      <StyledOverlay>
        <Helmet>
          <title>Invitation Already Accepted</title>
        </Helmet>
        <StyledCard>
          <StyledSuccessIcon>
            <CheckCircleIcon />
          </StyledSuccessIcon>
          <StyledTitle>Already Accepted</StyledTitle>
          <StyledMessage>
            This invitation to join <strong>{invitation.organizationName}</strong> has already been accepted.
          </StyledMessage>
          {currentUser ? (
            <StyledLink to={Routes.dash()}>Go to Dashboard</StyledLink>
          ) : (
            <StyledLink to={Routes.signIn()}>Sign In</StyledLink>
          )}
        </StyledCard>
      </StyledOverlay>
    )
  }

  // Success state after accepting
  if (acceptSuccess) {
    return (
      <StyledOverlay>
        <Helmet>
          <title>Invitation Accepted</title>
        </Helmet>
        <StyledCard>
          <StyledSuccessIcon>
            <CheckCircleIcon />
          </StyledSuccessIcon>
          <StyledTitle>Welcome!</StyledTitle>
          <StyledMessage>
            You have successfully joined <strong>{invitation.organizationName}</strong>.
          </StyledMessage>
          <StyledLink to={Routes.dash()}>Go to Dashboard</StyledLink>
        </StyledCard>
      </StyledOverlay>
    )
  }

  // Pending invitation - need to accept
  // If user is not logged in, redirect to signup with invitation token
  if (!currentUser) {
    return <Redirect to={`/sign-up?invitation=${token}`} />
  }

  // User is logged in - check email match
  const emailMatches =
    !invitation.invitedEmail || invitation.invitedEmail.toLowerCase() === currentUser.email?.toLowerCase()

  if (!emailMatches) {
    return (
      <StyledOverlay>
        <Helmet>
          <title>Email Mismatch</title>
        </Helmet>
        <StyledCard>
          <StyledWarningIcon>
            <AlertIcon />
          </StyledWarningIcon>
          <StyledTitle>Email Mismatch</StyledTitle>
          <StyledMessage>
            This invitation was sent to <strong>{invitation.invitedEmail}</strong>, but you are signed in as{' '}
            <strong>{currentUser.email}</strong>.
          </StyledMessage>
          <StyledSubMessage>Please sign in with the correct account or contact the organization.</StyledSubMessage>
          <StyledButtonGroup>
            <StyledSecondaryButton onClick={() => (window.location.href = '/sign-out')}>Sign Out</StyledSecondaryButton>
            <StyledLink to={Routes.dash()}>Go to Dashboard</StyledLink>
          </StyledButtonGroup>
        </StyledCard>
      </StyledOverlay>
    )
  }

  // Show accept invitation form
  return (
    <StyledOverlay>
      <Helmet>
        <title>Accept Invitation - {invitation.organizationName}</title>
      </Helmet>
      <StyledCard>
        <StyledOrgIcon>
          <BuildingIcon />
        </StyledOrgIcon>
        <StyledTitle>You&apos;re Invited!</StyledTitle>
        <StyledMessage>
          {invitation.invitedByName ? (
            <>
              <strong>{invitation.invitedByName}</strong> has invited you to join{' '}
              <strong>{invitation.organizationName}</strong>.
            </>
          ) : (
            <>
              You&apos;ve been invited to join <strong>{invitation.organizationName}</strong>.
            </>
          )}
        </StyledMessage>

        {acceptError && <StyledError>{acceptError}</StyledError>}

        <StyledAcceptButton onClick={handleAccept} disabled={acceptLoading}>
          {acceptLoading ? 'Accepting...' : 'Accept Invitation'}
        </StyledAcceptButton>

        <StyledDeclineText>
          <StyledLink to={Routes.dash()}>Maybe Later</StyledLink>
        </StyledDeclineText>
      </StyledCard>
    </StyledOverlay>
  )
}

export default AcceptInvitation

// Icons
const CheckCircleIcon: React.FC = () => <Hugeicon name="checkmark-circle" size={48} />

const XCircleIcon: React.FC = () => <Hugeicon name="cancel-circle" size={48} />

const ClockIcon: React.FC = () => <Hugeicon name="clock" size={48} />

const AlertIcon: React.FC = () => <Hugeicon name="alert" size={48} />

const BuildingIcon: React.FC = () => <Hugeicon name="bank" size={48} />

// Styled Components
const StyledOverlay = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1e3a5f 0%, #0f2439 100%);
  padding: 20px;
`

const StyledCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px 40px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`

const StyledOrgIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin: 0 auto 24px;
`

const StyledSuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin: 0 auto 24px;
`

const StyledErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin: 0 auto 24px;
`

const StyledWarningIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin: 0 auto 24px;
`

const StyledTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 16px;
`

const StyledMessage = styled.p`
  font-size: 16px;
  color: #4b5563;
  margin: 0 0 8px;
  line-height: 1.5;

  strong {
    color: #102638;
  }
`

const StyledSubMessage = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px;
`

const StyledError = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin: 16px 0;
  font-size: 14px;
`

const StyledAcceptButton = styled.button`
  background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
  margin-top: 24px;
  width: 100%;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(168, 85, 247, 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

const StyledLink = styled(Link)`
  display: inline-block;
  color: #a855f7;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  margin-top: 16px;

  &:hover {
    text-decoration: underline;
  }
`

const StyledDeclineText = styled.div`
  margin-top: 8px;
`

const StyledButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: center;
`

const StyledSecondaryButton = styled.button`
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease-out;

  &:hover {
    background: #e5e7eb;
  }
`
